import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild, ViewEncapsulation } from '@angular/core';
import { GridStack } from 'gridstack';
import { GridstackComponent, NgGridStackOptions, NgGridStackWidget, nodesCB } from 'gridstack/dist/angular';
import { DashboardContext, DashboardService, KpiSlicerBaseData, KpiTypes } from './dashboard.service';
import { DashboardWidgetSlicer, WidgetTypes } from './dashboard-widget-base/dashboard-widget-base.component';
import { BehaviorSubject, debounceTime, Subject } from 'rxjs';
import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import { UtilityService } from '@app/core/services/utility.service';
import { SlicerSubjectData } from './slicers/dashboard-slicer-widget-base/dashboard-slicer-widget-base.component';
import { RememberService } from '@app/core/services/remember.service';
import { ComponentBaseComponent } from '../../component-base/component-base.component';
import { TranslateService } from '@app/core/services/translate.service';

@Component({
    selector: 'tt-webpagemenu-dashboard',
    templateUrl: './webpagemenu-dashboard.component.html',
    styleUrls: ['./webpagemenu-dashboard.component.css'],
    encapsulation: ViewEncapsulation.None,
})
export class WebpagemenuDashboardComponent extends ComponentBaseComponent implements AfterViewInit, OnChanges, OnInit {
    /**
     * The name of the webpagemenu dashboard to use for widget/layout lookup.
     */
    @Input()
    ttName!: string;

    /**
     * The type of entity/context to use when loading widget data.
     */
    @Input()
    ttArgtype?: string;

    /**
     * The value of the entity/context to use when loading widget data.
     */
    @Input()
    ttArgvalue?: string | number;

    /**
     * Title to display for the dashboard, if any.
     */
    @Input()
    ttTitle?: string;

    /**
     * Whether the dashboard is currently editable.
     */
    @Input()
    public get ttEdit(): boolean {
        return this._editing;
    }
    public set ttEdit(value: BooleanInput) {
        this._editing = coerceBooleanProperty(value);
    }
    private _editing = false;

    /**
     * @default false
     */
    @Input()
    public get ttShowHeadingButtons(): boolean {
        return this._showHeadingButtons;
    }
    public set ttShowHeadingButtons(value: BooleanInput) {
        this._showHeadingButtons = coerceBooleanProperty(value);
    }
    private _showHeadingButtons = false;

    /**
     * Whether the dashboard is currently editable.
     */
    @Output()
    public ttEditChange = new EventEmitter<boolean>();

    /**
     * The selected widget when the dashboard is editable.
     */
    @Input()
    public ttSelectedWidget: string = '';

    /**
     * The the current selected widget has changed.
     */
    @Output()
    public ttSelectedWidgetChange = new EventEmitter<string>();

    /**
     * The selected widget when the dashboard is editable.
     */
    @Input()
    public ttReloadingData: boolean = false;

    /**
     * The the current selected widget has changed.
     */
    @Output()
    public ttReloadingDataChange = new EventEmitter<boolean>();

    @Output()
    public ttReady = new EventEmitter<WebpagemenuDashboardComponent>();

    /**
     * The gridstack component.
     */
    @ViewChild(GridstackComponent)
    public gridComp?: GridstackComponent;

    /**
     * The list over keynos for the kpi-comps to render of on this webpagemenu dashboard.
     */
    public kpiCompKeynos: KpiTypes[] = [];

    /**
     * List of slicers to be displayed for this webpagemenu dashboard.
     */
    private slicers: KpiSlicerBaseData[] = [];

    private slicersChanged = new BehaviorSubject<SlicerSubjectData[]>([]);

    // @ts-ignore
    public widgets: NgGridStackWidget[] = [];

    /**
     * Reference to the gridstack instance.
     */
    private gridRef?: GridStack;

    /**
     * Observable for which widget was last clicked.
     */
    private widgetClicked = new Subject<WidgetTypes>();

    private forceRefreshSubject = new Subject<void>();

    private currentDataContext?: DashboardContext;

    public translations: { [key: string]: string } = {
        edit_dashboard_layout: '',
        stop_editing: '',
        reload_data: '',
    };

    public translateFinished = false;

    /**
     * Initial configuration of the gridstack.
     */
    public gridOptions: NgGridStackOptions = {
        sizeToContent: false,
        cellHeightThrottle: 0,
        animate: true,
        margin: 20,
        class: 'well',
        cellHeight: '5rem',
        disableResize: true,
        float: false,
        staticGrid: true,
        columnOpts: {
            layout: 'none',
            breakpointForWindow: true,
            breakpoints: [
                { w: 700, c: 3 },
                { w: 1100, c: 6 },
            ],
        },
    };

    constructor(
        private elementRef: ElementRef,
        private dashboard: DashboardService,
        private utility: UtilityService,
        private remember: RememberService,
        private translate: TranslateService,
    ) {
        super();
    }

    /**
     * Creates grid-stack item options for a dashboardwidget of the given kpi-comp keyno.
     *
     * @param kpiCompKeyno the kpi comp keyno of the widget.
     * @returns configuration option for a grid-stack item rendering the given kpi-comp keyno widget.
     */
    private createDashboardWidget(kpiComp: KpiTypes) {
        const dashboardName = this.getDashboardName();
        const context = this.getCurrentDataContext();
        const id = this.getWidgetId(kpiComp.kpicomp_keyno);

        if (kpiComp.tile_type === 'slicer') {
            return <NgGridStackWidget>{
                id: id,
                selector: 'tt-dashboard-widget-base',
                h: !!kpiComp.h ? +kpiComp.h : 3,
                w: !!kpiComp.w ? +kpiComp.w : 3,
                x: !!kpiComp.x ? +kpiComp.x : undefined,
                y: !!kpiComp.y ? +kpiComp.y : undefined,
                input: { ttDashboardWidget: <DashboardWidgetSlicer>{ ...kpiComp, dashboardName: dashboardName, dashboardContext: context, api: () => this.gridRef!, widgetClicked: () => this.widgetClicked, slicersChanged: () => this.slicersChanged } },
            };
        } else {
            return <NgGridStackWidget>{
                id: id,
                selector: 'tt-dashboard-widget-base',
                h: !!kpiComp.h ? +kpiComp.h : 3,
                w: !!kpiComp.w ? +kpiComp.w : 3,
                x: !!kpiComp.x ? +kpiComp.x : undefined,
                y: !!kpiComp.y ? +kpiComp.y : undefined,
                input: { ttDashboardWidget: { kpicomp_keyno: kpiComp.kpicomp_keyno, kpislicers: kpiComp.kpislicers, dashboardName: dashboardName, dashboardContext: context, api: () => this.gridRef!, widgetClicked: () => this.widgetClicked, slicersChanged: () => this.slicersChanged, forceRefreshSubject: () => this.forceRefreshSubject, onLoaded: () => this.handleWidgetLoaded(id) } },
            };
        }
    }

    public async refreshAllWidgets() {
        this.ttReloadingData = true;
        this.ttReloadingDataChange.emit(this.ttReloadingData);
        this.allWidgetsLoaded$.next(false);
        this.forceRefreshSubject.next();
    }

    private widgetLoadStatus = new Map<string, boolean>();

    allWidgetsLoaded$ = new BehaviorSubject<boolean>(true);

    private handleWidgetLoaded(id: string) {
        this.widgetLoadStatus.set(id, true);

        const allLoaded = Array.from(this.widgetLoadStatus.values()).every((status) => status);

        if (allLoaded) {
            this.allWidgetsLoaded$.next(true);
            this.ttReloadingData = false;
            this.ttReloadingDataChange.emit(this.ttReloadingData);
        }
    }

    private async translateWords() {
        try {
            const data: { [key: string]: string } = await this.translate.translateBatch(Object.keys(this.translations));

            Object.keys(data).forEach((key) => (this.translations[key] = data[key]));
        } finally {
            this.translateFinished = true;
        }
    }

    /**
     * Loads the kpi comp keynos to this component.
     */
    private async loadKpiCompKeynos() {
        this.kpiCompKeynos = await this.dashboard.getDashboardWidgetKeynos(this.getDashboardName());
    }

    private async loadPreviousSlicerSettings() {
        const response = (await this.remember.getLastStatus(this.getSlicerRememberKey()))?.[0]?.variablevalue ?? '';

        if (!response) {
            this.slicersChanged.next([]);
            return;
        }

        try {
            const data = this.base64UrlDecode(response);
            const slicerdata = JSON.parse(data);

            this.slicersChanged.next(slicerdata);
        } catch (error) {
            console.error(error);
        }
    }

    private rememberSlicerSettings(data: SlicerSubjectData[]) {
        try {
            this.remember.remember(this.getSlicerRememberKey(), this.base64UrlEncodeUnicode(JSON.stringify(data)));
        } catch (error) {
            console.error(error);
        }
    }

    /**
     * Loads the widgets to the gridstack. Assumes that the keynos are already loaded.
     */
    private loadWidgets() {
        if (!!this.gridComp?.grid) {
            this.gridRef?.removeAll();

            this.widgets = this.kpiCompKeynos?.map((data) => this.createDashboardWidget(data)) ?? [];

            this.gridRef = this.gridComp.grid.load(this.widgets);
            this.gridRef?.onResize(document.body.clientWidth);
        }
    }

    public editing = false;

    public stopEditing() {
        setTimeout(() => (this.editing = false));
    }

    async persistChange(event: nodesCB) {
        if (this.editing === true) {
            const dashboardName = this.getDashboardName();

            await Promise.all(
                event.nodes.map(async (node: NgGridStackWidget) => {
                    const kpiPosition = { kpicomp_keyno: +node.input!['ttDashboardWidget']!.kpicomp_keyno!, webpage_name: dashboardName, h: node.h!, w: node.w!, x: node.x!, y: node.y!, layout_columns: this.gridRef?.engine.column };

                    this.dashboard.setWidgetPosition(dashboardName, { kpicomp_keyno: '' + kpiPosition.kpicomp_keyno, h: '' + kpiPosition.h, w: '' + kpiPosition.w, x: '' + kpiPosition.x, y: '' + kpiPosition.y });
                    this.dashboard.saveWidgetPosition(kpiPosition);
                }),
            );
        }
    }

    /**
     * Handles when a widget for inside this webpage-dashboard has been clicked.
     *
     * @param widget the widget which was clicked.
     */
    private onWidgetClicked(widget: WidgetTypes) {
        if (this.ttEdit) {
            this.ttSelectedWidget = '' + widget.kpicomp_keyno!;
            this.ttSelectedWidgetChange.emit(this.ttSelectedWidget);
        }
    }

    public onEditChange(event: BooleanInput) {
        this.ttEdit = event;

        if (this.ttEdit) {
            this.gridRef?.setStatic(false);
            this.gridRef?.enableResize(true);
            this.gridRef?.enableMove(true);
        } else {
            this.gridRef?.setStatic(true);
            this.gridRef?.enableResize(false);
            this.gridRef?.enableMove(false);
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['ttEdit']) {
            this.onEditChange(changes['ttEdit'].currentValue);
        }

        if (changes['ttShowHeadingButtons']) {
            this.ttShowHeadingButtons = changes['ttShowHeadingButtons'].currentValue;
        }

        if ((changes['ttArgtype'] || changes['ttArgvalue'] || changes['ttName']) && this.currentDataContext) {
            this.loadDashboard();
        }
    }

    async ngOnInit(): Promise<void> {
        this.translateWords();
        this.widgetClicked.pipe(debounceTime(250)).subscribe({ next: (widget) => this.onWidgetClicked(widget) });
        this.slicersChanged.pipe(debounceTime(250)).subscribe({ next: (data) => this.rememberSlicerSettings(data) });

        await this.loadDashboard();
    }

    async ngAfterViewInit(): Promise<void> {
        this.loadWidgets();
    }

    private async loadDashboard() {
        this.currentDataContext = this.resolveDataContext();
        this.widgetLoadStatus.clear();
        this.allWidgetsLoaded$.next(true);

        await Promise.all([this.loadKpiCompKeynos(), this.loadPreviousSlicerSettings()]);
        this.loadWidgets();
        this.ttReady.emit(this);
    }

    private resolveDataContext(): DashboardContext {
        if (!!this.ttArgtype && this.ttArgvalue !== undefined && this.ttArgvalue !== null) {
            return { argtype: this.ttArgtype, argvalue: this.ttArgvalue };
        }

        return { argtype: 'webpage_name', argvalue: this.getDashboardName() };
    }

    private getDashboardName(): string {
        if (!!this.ttName) {
            return this.ttName;
        }

        if (this.ttArgtype === 'webpage_name' && this.ttArgvalue !== undefined && this.ttArgvalue !== null) {
            return '' + this.ttArgvalue;
        }

        throw Error('Dashboard requires ttName to choose widgets/layout.');
    }

    private getCurrentDataContext(): DashboardContext {
        return this.currentDataContext ?? this.resolveDataContext();
    }

    private getDataContextKey(): string {
        return this.dashboard.getDashboardContextKey(this.getCurrentDataContext());
    }

    private getWidgetId(kpiCompKeyno: string | number): string {
        return `${this.getDashboardName()}.${kpiCompKeyno}`;
    }

    private getSlicerRememberKey(): string {
        const dashboardName = this.getDashboardName();
        const context = this.getCurrentDataContext();

        if (context.argtype === 'webpage_name' && '' + context.argvalue === dashboardName) {
            return `dashboard.slicerdata.${dashboardName}`;
        }

        return `dashboard.slicerdata.${dashboardName}.${this.getDataContextKey()}`;
    }
}
